<script lang="ts">
  import type { HourlyPeriod } from '../../api/types'
  import { HOURLY_CELLS, hourLabels, rainFraction } from '../../forecast/hourly'

  /**
   * "Next 24 hours" as a sideways strip of 67 px cells (Phase 10 plan §4.3,
   * Phase 11's spacing):
   * the time, the temperature, a rain bar and its figure. Times are in the
   * point's zone, the meteogram's rule, and a cell that starts a new local
   * day carries the weekday.
   *
   * On desktop the strip keeps a thin scrollbar, because a mouse has no other
   * cue that it scrolls; on touch it has none.
   */

  let { hourly, timezone }: { hourly: HourlyPeriod[]; timezone: string | null } = $props()

  const uid = $props.id()

  let hours = $derived(hourly.slice(0, HOURLY_CELLS))
  let labels = $derived(hourLabels(hours, timezone))
</script>

{#if hours.length > 0}
  <section class="hourly" aria-labelledby="{uid}-title">
    <h2 id="{uid}-title" class="wx-sec-head section-title">Next 24 hours</h2>
    <!-- Focusable, so a keyboard can scroll it: it is a region that
         overflows, not a list of controls. -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div class="strip" tabindex="0" role="group" aria-labelledby="{uid}-title">
      <ol>
        {#each hours as hour, i (hour.start ?? i)}
          {@const pop = rainFraction(hour.precip_probability)}
          {@const label = labels[i]}
          <li class:day-start={label.day !== null}>
            <span class="time"
              >{#if label.day}<b>{label.day}</b>{/if}{#if label.midnight}<span
                  class="visually-hidden">{` ${label.time}`}</span
                >{:else}{label.day ? ` ${label.time}` : label.time}{/if}</span
            >
            <span class="temp">{hour.temp ?? '—'}°</span>
            <!-- A null chance is no bar and no figure, which says something
                 different from an empty box and "0%". -->
            <span class="box" class:none={pop === null} aria-hidden="true">
              {#if pop !== null}<span class="fill" style:height="{pop * 100}%"></span>{/if}
            </span>
            <span class="pop" class:none={pop === null}>
              {#if pop !== null}{hour.precip_probability}%<span class="visually-hidden">
                  chance of rain</span
                >{/if}
            </span>
          </li>
        {/each}
      </ol>
    </div>
  </section>
{/if}

<style>
  /* Phase 11's section head; the strip under it runs on to the right edge
     and the head stops at the gutter. */
  .section-title {
    margin: 30px var(--gutter, 0px) 0;
  }

  .strip {
    /* The containing block for the `.visually-hidden` spans in each hour.
       Without it they are placed against the sidebar at their spot along the
       unscrolled strip, and the sidebar grew 1,000 px of sideways scroll. */
    position: relative;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
    margin: 0 0 0 var(--gutter, 0px);
    border-bottom: 1px solid var(--color-divider);
  }

  .strip:focus-visible {
    outline-offset: -2px;
  }

  @media (pointer: coarse) {
    .strip {
      scrollbar-width: none;
    }

    .strip::-webkit-scrollbar {
      display: none;
    }
  }

  ol {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  /* The redesign's `.hour`: 3 px rules between the hours, a 40 x 52 chance
     bar on the track. */
  li {
    flex: none;
    width: 67px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 0 14px 12px;
    border-right: 3px solid var(--color-rule);
    scroll-snap-align: start;
  }

  /* The day boundary, drawn as a rule in the ink: the strip's one cue that
     the evening has turned into tomorrow. */
  .day-start {
    box-shadow: inset 3px 0 0 var(--color-text);
  }

  .time {
    font-size: 14px;
    white-space: nowrap;
    color: var(--color-muted);
  }

  .time b {
    font-weight: 800;
    color: var(--color-text);
  }

  .temp {
    font-size: 24px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .box {
    width: 40px;
    height: 52px;
    display: flex;
    align-items: flex-end;
    background: var(--color-track);
  }

  .box.none {
    visibility: hidden;
  }

  .fill {
    width: 100%;
    background: var(--color-rain);
  }

  /* Rain's mark colour, which is also 4.5:1 as text on the ground. */
  .pop {
    font-size: 14px;
    font-weight: 800;
    color: var(--color-rain);
    font-variant-numeric: tabular-nums;
    min-height: 1.3em;
  }
</style>
